import React, { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import {
  Card,
  Spin,
  Tabs,
  Typography,
  Button,
  Modal,
  Select,
  DatePicker,
  Row,
  Col,
  Checkbox,
  message,
} from "antd";
import {
  FileTextOutlined,
  UserOutlined,
  TeamOutlined,
  DollarOutlined,
  DownloadOutlined,
} from "@ant-design/icons";
import dayjs from "dayjs";
import * as XLSX from "xlsx";
import * as customerService from "../Customers/services/customerService";
import * as jobService from "../Jobs/services/jobService";
import * as employeeService from "../Employees/services/employeeService";
import * as expenseService from "../Expenses/services/expenseService";
import * as contractService from "../Contracts/services/contractService";
import * as accountSettingsService from "../../services/accountSettingsService";
import { useAuth } from "../../AuthContext";
import config from "../../config";
import { resolveLogoUrl } from "../../utils/resolveLogoUrl";
import JobsView from "./components/views/JobsView";
import CustomersView from "./components/views/CustomersView";
import EmployeesView from "./components/views/EmployeesView";
import ExpensesView from "./components/views/ExpensesView";
import "./reports.css";

const { Title, Text } = Typography;
const { RangePicker } = DatePicker;
const { Option } = Select;

const Reports = () => {
  const [activeTab, setActiveTab] = useState("jobs");
  const [exportModalVisible, setExportModalVisible] = useState(false);
  const [selectedExportTypes, setSelectedExportTypes] = useState(["jobs"]);
  const [exportDateRange, setExportDateRange] = useState([
    dayjs().subtract(30, "day"),
    dayjs(),
  ]);
  const { token } = useAuth();

  // Fetch customers
  const { data: customersData, isLoading: customersLoading } = useQuery({
    queryKey: ["customers"],
    queryFn: () => customerService.getCustomersList({ pageSize: 10000 }),
  });

  // Fetch jobs
  const { data: jobsData, isLoading: jobsLoading } = useQuery({
    queryKey: ["jobs"],
    queryFn: () =>
      jobService.getJobsList({
        searchTerm: "",
        page: 0,
        pageSize: 10000,
        statusFilter: "All",
      }),
  });

  // Fetch employees
  const { data: employeesData, isLoading: employeesLoading } = useQuery({
    queryKey: ["employees"],
    queryFn: () => employeeService.getAllEmployees(),
  });

  // Fetch expenses
  const { data: expensesData, isLoading: expensesLoading } = useQuery({
    queryKey: ["expenses"],
    queryFn: () =>
      expenseService.getExpensesList({
        page: 0,
        pageSize: 10000,
        searchTerm: "",
        typeFilter: "All",
      }),
  });

  // Fetch contracts
  const { data: contractsData, isLoading: contractsLoading } = useQuery({
    queryKey: ["contracts"],
    queryFn: () =>
      contractService.getContractsList({
        page: 0,
        pageSize: 10000,
        searchTerm: "",
      }),
  });

  // Fetch account settings for company branding
  const { data: accountSettings } = useQuery({
    queryKey: ["accountSettings", token],
    queryFn: () => accountSettingsService.getAccountSettings(),
    enabled: !!token,
  });

  const companyName = accountSettings?.companyName || "Report Center";
  const companyLogoUrl = resolveLogoUrl(accountSettings?.logoUrl);

  // Extract data from responses
  const customers = customersData?.customers || [];
  const jobs = jobsData?.jobs || [];
  const employees = employeesData?.employees || [];
  const expenses = expensesData?.expenses || [];
  const contracts = contractsData?.contracts || [];

  // Export function with summary calculations
  const handleExport = () => {
    try {
      if (selectedExportTypes.length === 0) {
        message.error("Please select at least one data type to export");
        return;
      }

      const startDate = exportDateRange?.[0];
      const endDate = exportDateRange?.[1];

      const filterByDate = (date) => {
        if (!startDate || !endDate) return true;
        if (!date) return false;
        const itemDate = dayjs(date);
        return (
          (itemDate.isAfter(startDate) || itemDate.isSame(startDate, "day")) &&
          (itemDate.isBefore(endDate) || itemDate.isSame(endDate, "day"))
        );
      };

      const workbook = XLSX.utils.book_new();
      const summaryData = [];
      let totalSheets = 0;

      // Orders Summary & Data (renamed from Jobs)
      if (selectedExportTypes.includes("jobs")) {
        const filteredJobs = jobs.filter((job) => filterByDate(job.startDate));
        const jobsByStatus = {};
        const jobsByFulfillment = {};
        let totalProductCost = 0;
        let totalTaxAmount = 0;
        let totalPaidAmount = 0;

        filteredJobs.forEach((job) => {
          const status = job.status || "Unknown";
          jobsByStatus[status] = (jobsByStatus[status] || 0) + 1;

          const fulfillmentStatus = job.fulfillmentStatus || "PENDING";
          jobsByFulfillment[fulfillmentStatus] = (jobsByFulfillment[fulfillmentStatus] || 0) + 1;

          // Calculate product cost from job.sales (products added to order)
          let productCost = 0;
          if (job.sales && Array.isArray(job.sales)) {
            job.sales.forEach((sale) => {
              if (sale.saleItems && Array.isArray(sale.saleItems)) {
                sale.saleItems.forEach((saleItem) => {
                  const unitPrice = parseFloat(saleItem.unitPrice || saleItem.product?.wholesalePrice || saleItem.product?.unitPrice || 0);
                  const quantity = parseInt(saleItem.quantity) || 1;
                  productCost += unitPrice * quantity;
                });
              }
            });
          }

          const reportTaxRate = accountSettings?.taxRate ? parseFloat(accountSettings.taxRate) / 100 : 0.06;
          const taxAmount = job.includeTax ? productCost * reportTaxRate : 0;

          totalProductCost += productCost;
          totalTaxAmount += taxAmount;

          // Calculate payments (filter by date range)
          if (job.payments && Array.isArray(job.payments)) {
            job.payments.forEach((payment) => {
              if (filterByDate(payment.paymentDate)) {
                totalPaidAmount += parseFloat(payment.amount) || 0;
              }
            });
          }
        });

        const totalRevenue = totalProductCost + totalTaxAmount;
        const totalOutstanding = Math.max(0, totalRevenue - totalPaidAmount);

        summaryData.push(
          { Category: "WHOLESALE ORDERS SUMMARY", Metric: "", Value: "" },
          { Category: "", Metric: "Total Orders", Value: filteredJobs.length },
          ...Object.entries(jobsByStatus).map(([status, count]) => ({
            Category: "",
            Metric: `Orders - ${status}`,
            Value: count,
          })),
          { Category: "", Metric: "", Value: "" },
          { Category: "FULFILLMENT STATUS", Metric: "", Value: "" },
          ...Object.entries(jobsByFulfillment).map(([status, count]) => ({
            Category: "",
            Metric: status,
            Value: count,
          })),
          { Category: "", Metric: "", Value: "" },
          { Category: "REVENUE BREAKDOWN", Metric: "", Value: "" },
          { Category: "", Metric: "Total Product Sales", Value: `$${totalProductCost.toFixed(2)}` },
          { Category: "", Metric: `Total Tax (${accountSettings?.taxRate || 6}%)`, Value: `$${totalTaxAmount.toFixed(2)}` },
          { Category: "", Metric: "TOTAL REVENUE", Value: `$${totalRevenue.toFixed(2)}` },
          { Category: "", Metric: "", Value: "" },
          { Category: "PAYMENT STATUS", Metric: "", Value: "" },
          { Category: "", Metric: "Total Paid", Value: `$${totalPaidAmount.toFixed(2)}` },
          { Category: "", Metric: "Total Outstanding", Value: `$${totalOutstanding.toFixed(2)}` },
          { Category: "", Metric: "", Value: "" },
          { Category: "AVERAGES", Metric: "", Value: "" },
          { Category: "", Metric: "Average Order Value", Value: filteredJobs.length > 0 ? `$${(totalRevenue / filteredJobs.length).toFixed(2)}` : "$0.00" },
          { Category: "", Metric: "", Value: "" }
        );

        // First pass: collect all unique products across all jobs
        const allProductsMap = new Map();
        filteredJobs.forEach((job) => {
          if (job.sales && Array.isArray(job.sales)) {
            job.sales.forEach((sale) => {
              if (sale.saleItems && Array.isArray(sale.saleItems)) {
                sale.saleItems.forEach((saleItem) => {
                  if (saleItem.product) {
                    allProductsMap.set(saleItem.product.id, saleItem.product.name);
                  }
                });
              }
            });
          }
        });
        const allProducts = Array.from(allProductsMap.entries()).sort((a, b) =>
          a[1].localeCompare(b[1])
        ); // Sort by product name

        // Add detailed orders sheet with comprehensive breakdown
        const jobsExportData = filteredJobs.map((job) => {
          // Calculate product cost from job.sales
          let productCost = 0;
          let productCount = 0;
          const productQuantities = new Map(); // Track quantity per product

          if (job.sales && Array.isArray(job.sales)) {
            job.sales.forEach((sale) => {
              if (sale.saleItems && Array.isArray(sale.saleItems)) {
                sale.saleItems.forEach((saleItem) => {
                  const unitPrice = parseFloat(saleItem.unitPrice || saleItem.product?.wholesalePrice || saleItem.product?.unitPrice || 0);
                  const quantity = parseInt(saleItem.quantity) || 1;
                  productCost += unitPrice * quantity;
                  productCount += quantity;

                  // Track quantity per product
                  if (saleItem.product) {
                    const productId = saleItem.product.id;
                    productQuantities.set(productId, (productQuantities.get(productId) || 0) + quantity);
                  }
                });
              }
            });
          }

          const exportTaxRate = accountSettings?.taxRate ? parseFloat(accountSettings.taxRate) / 100 : 0.06;
          const taxAmount = job.includeTax ? productCost * exportTaxRate : 0;
          const totalCost = productCost + taxAmount;
          // Filter payments by date range
          const paidAmount = job.payments?.reduce((sum, p) => {
            if (filterByDate(p.paymentDate)) {
              return sum + (parseFloat(p.amount) || 0);
            }
            return sum;
          }, 0) || 0;
          const outstanding = Math.max(0, totalCost - paidAmount);

          // Get product list
          const productList = [];
          if (job.sales && Array.isArray(job.sales)) {
            job.sales.forEach((sale) => {
              if (sale.saleItems && Array.isArray(sale.saleItems)) {
                sale.saleItems.forEach((saleItem) => {
                  productList.push(`${saleItem.product?.name || 'Unknown'} (${saleItem.quantity}x)`);
                });
              }
            });
          }

          // Calculate days outstanding
          const daysOutstanding = job.startDate ? dayjs().diff(dayjs(job.startDate), 'day') : 0;

          // Get last payment date
          const lastPaymentDate = job.payments && job.payments.length > 0
            ? dayjs(job.payments[job.payments.length - 1].paymentDate).format("YYYY-MM-DD")
            : "";

          const rowData = {
            "Order ID": job.id,
            Title: job.title,
            Customer: job.customer?.name || "",
            "Customer Email": job.customer?.email || "",
            "Customer Phone": job.customer?.phone || "",
            Status: job.status,
            "Fulfillment Status": job.fulfillmentStatus || "PENDING",
            "Payment Status": paidAmount >= totalCost ? "PAID" : paidAmount > 0 ? "PARTIAL" : "UNPAID",
            "Invoice Date": job.startDate ? dayjs(job.startDate).format("YYYY-MM-DD") : "",
            "Completion Date": job.endDate ? dayjs(job.endDate).format("YYYY-MM-DD") : "",
            "Days Outstanding": daysOutstanding,
            "Last Payment Date": lastPaymentDate,
            "Tracking Number": job.trackingNumber || "",
            "Shipping Carrier": job.shippingCarrier || "",
            "Products": productList.join(", "),
            "Product Count": productCount,
          };

          // Add individual product quantity columns
          allProducts.forEach(([productId, productName]) => {
            rowData[productName] = productQuantities.get(productId) || 0;
          });

          // Add financial columns after product columns
          rowData["Product Cost"] = productCost.toFixed(2);
          rowData["Tax Included"] = job.includeTax ? "Yes" : "No";
          rowData["Tax Rate"] = `${accountSettings?.taxRate || 6}%`;
          rowData["Tax Amount"] = taxAmount.toFixed(2);
          rowData["Total Cost"] = totalCost.toFixed(2);
          rowData["Amount Paid"] = paidAmount.toFixed(2);
          rowData["Outstanding"] = outstanding.toFixed(2);
          rowData["Payment Count"] = job.payments?.length || 0;

          return rowData;
        });
        if (jobsExportData.length > 0) {
          const jobsSheet = XLSX.utils.json_to_sheet(jobsExportData);
          XLSX.utils.book_append_sheet(workbook, jobsSheet, "Orders Detail");
          totalSheets++;
        }
      }

      // Customers Summary & Data
      if (selectedExportTypes.includes("customers")) {
        const filteredCustomers = customers.filter((c) => filterByDate(c.createdAt));

        summaryData.push(
          { Category: "CUSTOMERS SUMMARY", Metric: "", Value: "" },
          { Category: "", Metric: "Total Customers", Value: filteredCustomers.length },
          { Category: "", Metric: "New Customers (Period)", Value: filteredCustomers.length },
          { Category: "", Metric: "", Value: "" }
        );

        const customersData = filteredCustomers.map((customer) => ({
          "Customer ID": customer.id,
          Name: customer.name,
          Email: customer.email || "",
          Phone: customer.phone || "",
          Address: customer.addressLine1 || "",
          City: customer.city || "",
          State: customer.state || "",
        }));
        if (customersData.length > 0) {
          const customersSheet = XLSX.utils.json_to_sheet(customersData);
          XLSX.utils.book_append_sheet(workbook, customersSheet, "Customers Detail");
          totalSheets++;
        }
      }

      // Employees Summary & Data
      if (selectedExportTypes.includes("employees")) {
        const laborExpenses = expenses.filter(
          (e) => e.type === "LABOR" && filterByDate(e.expenseDate)
        );

        let totalHours = 0;
        let totalLaborCost = 0;
        const employeeStats = employees.map((employee) => {
          const employeeLabor = laborExpenses.filter(
            (e) => e.employeeName === employee.name
          );
          const hours = employeeLabor.reduce(
            (sum, e) => sum + (parseFloat(e.hoursWorked) || 0),
            0
          );
          const earnings = employeeLabor.reduce(
            (sum, e) => sum + (parseFloat(e.amount) || 0),
            0
          );
          totalHours += hours;
          totalLaborCost += earnings;
          return {
            "Employee ID": employee.id,
            Name: employee.name,
            Email: employee.email || "",
            Phone: employee.phone || "",
            "Total Hours": hours,
            "Total Earnings": earnings,
          };
        });

        summaryData.push(
          { Category: "EMPLOYEES SUMMARY", Metric: "", Value: "" },
          { Category: "", Metric: "Total Employees", Value: employees.length },
          { Category: "", Metric: "Total Hours Worked", Value: totalHours.toFixed(2) },
          { Category: "", Metric: "Total Labor Cost", Value: `$${totalLaborCost.toFixed(2)}` },
          { Category: "", Metric: "Average Hours/Employee", Value: employees.length > 0 ? (totalHours / employees.length).toFixed(2) : "0" },
          { Category: "", Metric: "", Value: "" }
        );

        if (employeeStats.length > 0) {
          const employeesSheet = XLSX.utils.json_to_sheet(employeeStats);
          XLSX.utils.book_append_sheet(workbook, employeesSheet, "Employees Detail");
          totalSheets++;
        }
      }

      // Expenses Summary & Data
      if (selectedExportTypes.includes("expenses")) {
        const filteredExpenses = expenses.filter((e) => filterByDate(e.expenseDate));
        const expensesByType = {};
        let totalExpenseAmount = 0;

        filteredExpenses.forEach((expense) => {
          const type = expense.type || "OTHER";
          expensesByType[type] = (expensesByType[type] || 0) + (parseFloat(expense.amount) || 0);
          totalExpenseAmount += parseFloat(expense.amount) || 0;
        });

        summaryData.push(
          { Category: "EXPENSES SUMMARY", Metric: "", Value: "" },
          { Category: "", Metric: "Total Expenses", Value: filteredExpenses.length },
          { Category: "", Metric: "Total Amount", Value: `$${totalExpenseAmount.toFixed(2)}` },
          ...Object.entries(expensesByType).map(([type, amount]) => ({
            Category: "",
            Metric: `${type}`,
            Value: `$${amount.toFixed(2)}`,
          })),
          { Category: "", Metric: "", Value: "" }
        );

        const expensesData = filteredExpenses.map((expense) => ({
          "Expense ID": expense.id,
          Date: expense.expenseDate ? dayjs(expense.expenseDate).format("YYYY-MM-DD") : "",
          Type: expense.type,
          Description: expense.description || "",
          Vendor: expense.vendor || "",
          Amount: parseFloat(expense.amount) || 0,
          Employee: expense.employeeName || "",
          Job: expense.jobTitle || "",
        }));
        if (expensesData.length > 0) {
          const expensesSheet = XLSX.utils.json_to_sheet(expensesData);
          XLSX.utils.book_append_sheet(workbook, expensesSheet, "Expenses Detail");
          totalSheets++;
        }
      }

      // Add summary sheet at the beginning
      if (summaryData.length > 0) {
        // Add header info
        const headerData = [
          { Category: "BUSINESS REPORT", Metric: "", Value: "" },
          { Category: "Company", Metric: companyName, Value: "" },
          { Category: "Period", Metric: `${startDate?.format("MMM D, YYYY")} - ${endDate?.format("MMM D, YYYY")}`, Value: "" },
          { Category: "Generated", Metric: dayjs().format("MMM D, YYYY h:mm A"), Value: "" },
          { Category: "", Metric: "", Value: "" },
        ];
        const summarySheet = XLSX.utils.json_to_sheet([...headerData, ...summaryData]);
        XLSX.utils.book_append_sheet(workbook, summarySheet, "Summary");

        // Move summary to first position
        const sheetOrder = workbook.SheetNames;
        const summaryIndex = sheetOrder.indexOf("Summary");
        if (summaryIndex > 0) {
          sheetOrder.splice(summaryIndex, 1);
          sheetOrder.unshift("Summary");
          workbook.SheetNames = sheetOrder;
        }
      }

      if (totalSheets === 0 && summaryData.length === 0) {
        message.warning("No data found for the selected date range");
        return;
      }

      const filename = `business-report-${startDate?.format("YYYY-MM-DD")}-to-${endDate?.format("YYYY-MM-DD")}.xlsx`;
      XLSX.writeFile(workbook, filename);

      message.success(`Report exported successfully with ${selectedExportTypes.length} section(s)`);
      setExportModalVisible(false);
    } catch (error) {
      console.error("Error exporting:", error);
      message.error("Failed to export data");
    }
  };

  // Loading state
  const isLoading =
    customersLoading ||
    jobsLoading ||
    employeesLoading ||
    expensesLoading ||
    contractsLoading;

  if (isLoading) {
    return (
      <div className="w-full">
        <div className="text-center py-12">
          <Spin size="large" />
          <p className="mt-4 text-gray-600 text-lg">Loading reports data...</p>
        </div>
      </div>
    );
  }

  // Tab items
  const tabItems = [
    {
      key: "jobs",
      label: (
        <span className="flex items-center gap-2">
          <FileTextOutlined />
          Wholesale Orders
        </span>
      ),
      children: (
        <JobsView
          jobs={jobs}
          customers={customers}
          contracts={contracts}
          accountSettings={accountSettings}
          isLoading={jobsLoading}
        />
      ),
    },
    {
      key: "customers",
      label: (
        <span className="flex items-center gap-2">
          <UserOutlined />
          Customers
        </span>
      ),
      children: (
        <CustomersView
          customers={customers}
          jobs={jobs}
          accountSettings={accountSettings}
          isLoading={customersLoading}
        />
      ),
    },
    {
      key: "employees",
      label: (
        <span className="flex items-center gap-2">
          <TeamOutlined />
          Employees
        </span>
      ),
      children: (
        <EmployeesView
          employees={employees}
          expenses={expenses}
          isLoading={employeesLoading}
        />
      ),
    },
    {
      key: "expenses",
      label: (
        <span className="flex items-center gap-2">
          <DollarOutlined />
          Expenses
        </span>
      ),
      children: (
        <ExpensesView
          expenses={expenses}
          isLoading={expensesLoading}
        />
      ),
    },
  ];

  return (
    <div className="w-full" style={{ padding: '16px' }}>
      {/* Header */}
      <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 12 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          {companyLogoUrl && (
            <img
              src={companyLogoUrl}
              alt="Company Logo"
              style={{ height: 40, width: 40, objectFit: 'contain' }}
            />
          )}
          <div>
            <Title level={3} style={{ margin: 0 }}>
              {companyName}
            </Title>
            <Text type="secondary" style={{ fontSize: 13 }}>
              Reports
            </Text>
          </div>
        </div>
        <Button
          type="primary"
          size="large"
          icon={<DownloadOutlined />}
          onClick={() => setExportModalVisible(true)}
        >
          Export Report
        </Button>
      </div>

      {/* Main Tabs */}
      <Card size="small">
        <Tabs
          activeKey={activeTab}
          onChange={setActiveTab}
          items={tabItems}
        />
      </Card>

      {/* Export Modal */}
      <Modal
        title="Export Business Report"
        open={exportModalVisible}
        onCancel={() => setExportModalVisible(false)}
        onOk={handleExport}
        okText="Export"
        okButtonProps={{ icon: <DownloadOutlined /> }}
      >
        <div style={{ marginBottom: 16 }}>
          <Text strong style={{ display: 'block', marginBottom: 8 }}>
            Select Data Types (Multiple)
          </Text>
          <Select
            mode="multiple"
            value={selectedExportTypes}
            onChange={setSelectedExportTypes}
            style={{ width: '100%' }}
            size="large"
            placeholder="Select one or more data types"
            maxTagCount={2}
            maxTagPlaceholder={(omittedValues) => `+${omittedValues.length} more`}
          >
            <Option value="jobs">
              <FileTextOutlined style={{ marginRight: 8 }} />
              Wholesale Orders
            </Option>
            <Option value="customers">
              <UserOutlined style={{ marginRight: 8 }} />
              Customers
            </Option>
            <Option value="employees">
              <TeamOutlined style={{ marginRight: 8 }} />
              Employees
            </Option>
            <Option value="expenses">
              <DollarOutlined style={{ marginRight: 8 }} />
              Expenses
            </Option>
          </Select>
          <Text type="secondary" style={{ display: 'block', marginTop: 4, fontSize: 12 }}>
            Each selected type will have its own detail sheet plus a combined summary
          </Text>
        </div>

        <div style={{ marginBottom: 16 }}>
          <Text strong style={{ display: 'block', marginBottom: 8 }}>
            Date Range
          </Text>
          <RangePicker
            value={exportDateRange}
            onChange={setExportDateRange}
            style={{ width: '100%' }}
            size="large"
            placeholder={["Start Date", "End Date"]}
          />
          <Text type="secondary" style={{ display: 'block', marginTop: 4, fontSize: 12 }}>
            Data will be filtered based on: Jobs (start date), Customers (creation date), Employees (labor within period), Expenses (expense date)
          </Text>
        </div>

        {selectedExportTypes.length > 0 && (
          <div style={{ background: '#f5f5f5', padding: 12, borderRadius: 6 }}>
            <Text strong style={{ display: 'block', marginBottom: 8 }}>
              Export will include:
            </Text>
            <ul style={{ margin: 0, paddingLeft: 20 }}>
              <li>Summary sheet with totals and calculations</li>
              {selectedExportTypes.includes("jobs") && (
                <li>Orders: Product sales, fulfillment status, tax, payments, tracking info</li>
              )}
              {selectedExportTypes.includes("customers") && (
                <li>Customers: Total count, contact details</li>
              )}
              {selectedExportTypes.includes("employees") && (
                <li>Employees: Hours worked, earnings, labor costs</li>
              )}
              {selectedExportTypes.includes("expenses") && (
                <li>Expenses: Breakdown by type, total amounts</li>
              )}
            </ul>
          </div>
        )}
      </Modal>
    </div>
  );
};

export default Reports;
